import React from "react";
import { Box } from "theme-ui";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { themevals } from "theme/themevals";
import { TrendPoint } from "api/Dashboard";
import { Panel, EmptyState, compactCurrency, currency } from "./shared";

/**
 * Billed against collected, rolling 12 months.
 *
 * TWO LINES, NOT ONE. The current chart plots payments and calls them
 * revenue; the gap between what was billed and what came in is the whole
 * insight and is invisible with a single series.
 *
 * The window stays rolling whatever the period control says (spec 3.1) -
 * this is context, not measurement. Re-scoping it to the selected month
 * would leave a one-point chart.
 *
 * STRAIGHT SEGMENTS, NOT A SPLINE. These are twelve monthly totals, not
 * a continuous signal. A monotone curve invents shape between the points
 * - it overshoots peaks and draws a rise through months where nothing
 * happened - which reads as data the business does not have.
 */
const TrendChart: React.FC<{ trend: TrendPoint[] }> = ({ trend }) => {
  const hasData = trend.some((p) => p.invoiced > 0 || p.collected > 0);

  return (
    <Panel
      title="Billed vs collected"
      subtitle="Rolling 12 months. Not affected by the period filter."
      sx={{ flex: "2 1 460px", minWidth: "320px" }}
    >
      {!hasData ? (
        <EmptyState
          headline="No billing or collection in the last 12 months"
          detail="This is a real zero, not a loading state. The chart fills itself as invoices go out."
        />
      ) : (
        <Box sx={{ width: "100%", height: "240px" }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={trend} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EBEBEB" vertical={false} />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11, fontFamily: "Poppins, sans-serif" }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tickFormatter={compactCurrency}
                tick={{ fontSize: 11, fontFamily: "Poppins, sans-serif" }}
                axisLine={false}
                tickLine={false}
                width={56}
              />
              <Tooltip
                formatter={(value: any) => currency(Number(value))}
                contentStyle={{
                  fontFamily: "Poppins, sans-serif",
                  fontSize: "12px",
                  borderRadius: "6px",
                  border: "1px solid " + themevals.colors.inputOutline,
                }}
              />
              <Legend
                wrapperStyle={{ fontFamily: "Poppins, sans-serif", fontSize: "12px" }}
              />
              <Line
                type="linear"
                dataKey="invoiced"
                name="Billed"
                stroke={themevals.colors.info}
                strokeWidth={2}
                dot={{ r: 2 }}
              />
              <Line
                type="linear"
                dataKey="collected"
                name="Collected"
                stroke={themevals.colors.success}
                strokeWidth={2}
                dot={{ r: 2 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      )}
    </Panel>
  );
};

export default TrendChart;
